import BugIcon from "@mui/icons-material/BugReportOutlined";
import CheckIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorIcon from "@mui/icons-material/ErrorOutlineRounded";
import { CircularProgress, Stack, Tooltip } from "@mui/material";
import FileIcon from "@entrilia/shared/components/FileIcon";
import { getFileExtension } from "@entrilia/shared/utilities/fileHelper";
import GridTextHoverAction from "@entrilia/shared/components/grid/GridTextHoverAction";
import { useFilesContext } from "../FilesContext";
import type { FilesRowModel, UploadFileState } from "../filesState";

interface Props {
  row: FilesRowModel;
  fileName: string;
}

const FileStatusIcon = ({ uploadState }: { uploadState: UploadFileState }) => {
  switch (uploadState.status) {
    case "in_progress": {
      return <CircularProgress size={20} color="secondary" sx={{ flexShrink: 0 }} />;
    }
    case "completed": {
      return <CheckIcon color="success" />;
    }
    case "error": {
      if (uploadState.validationError !== undefined) {
        const errorText = uploadState.validationError || " This file cannot be uploaded";
        return (
          <Tooltip title={errorText} arrow>
            <ErrorIcon color="error" />
          </Tooltip>
        );
      }

      if (uploadState.uploadError?.type === "MalwareDetected") {
        return (
          <Tooltip title="Upload failed: malware detected" arrow>
            <BugIcon color="error" />
          </Tooltip>
        );
      }

      return (
        <Tooltip title="Upload failed" arrow>
          <ErrorIcon color="error" />
        </Tooltip>
      );
    }
  }
};

const FileNameCell = ({ row, fileName }: Props) => {
  const { onPreviewFile } = useFilesContext();

  if (!fileName) {
    return null;
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: "center", width: "100%", color: row.uploadState === undefined ? "inherit" : "text.secondary" }}
    >
      {row.uploadState === undefined && <FileIcon fileExtension={getFileExtension(fileName)} />}
      {row.uploadState !== undefined && <FileStatusIcon uploadState={row.uploadState} />}
      <GridTextHoverAction onClick={() => onPreviewFile(row)} actionDisabled={row.uploadState !== undefined}>
        {fileName}
      </GridTextHoverAction>
    </Stack>
  );
};

export default FileNameCell;
